<template>
  <div class="page providers-page">
    <div class="page-header">
      <h1 class="page-title">Equipe</h1>
      <p class="page-subtitle">Serviços e horários de trabalho por pessoa (para agendamento público)</p>
    </div>

    <div class="card-list">
      <article
        v-for="user in sortedUsers"
        :key="user.id"
        class="card card--with-icon"
      >
        <div
          class="card-icon-wrap providers-card-icon"
          :style="{ backgroundColor: user.color || '#ECD2D2' }"
        >
          <i class="material-icons">person</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ user.name || user.email || user.id }}</h3>
          <p v-if="(user.serviceIds || []).length" class="card-meta card-secondary">
            {{ (user.serviceIds || []).length }} serviço(s) · Horários configurados
          </p>
          <p v-else class="card-meta card-secondary">Nenhum serviço atribuído</p>
        </div>
        <div class="card-actions">
          <button
            type="button"
            class="btn-icon"
            aria-label="Editar serviços e horários"
            @click="openEdit(user)"
          >
            <i class="material-icons">edit</i>
          </button>
        </div>
      </article>
    </div>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingUser ? (editingUser.name || editingUser.id) : 'Providor' }}</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body" v-if="editingUser">
        <form @submit.prevent="save">
          <div class="form-group providers-percent-group">
            <label class="providers-percent-label" for="provider-percent-to-pay">Percentual a pagar</label>
            <p class="field-hint">Percentual padrão (0–100) para serviços sem valor específico abaixo.</p>
            <input
              id="provider-percent-to-pay"
              v-model.number="editingUser.percentToPay"
              type="number"
              min="0"
              max="100"
              step="1"
              class="providers-percent-input"
              placeholder="100"
              aria-label="Percentual a pagar"
            />
          </div>
          <div class="form-group providers-color-group">
            <label class="providers-color-label">Cor no calendário</label>
            <p class="field-hint">Cor usada para identificar esta pessoa nos agendamentos.</p>
            <div class="providers-color-picker-wrap">
              <input
                v-model="editingUser.color"
                type="color"
                class="providers-color-input"
                aria-label="Cor"
              />
              <input
                v-model="editingUser.color"
                type="text"
                class="providers-color-hex"
                placeholder="#b07d7d"
                maxlength="7"
                aria-label="Cor em hexadecimal"
              />
            </div>
            <div class="providers-color-presets">
              <button
                v-for="preset in colorPresets"
                :key="preset"
                type="button"
                class="providers-color-preset"
                :style="{ backgroundColor: preset }"
                :aria-label="'Usar cor ' + preset"
                @click="editingUser.color = preset"
              />
            </div>
          </div>
          <div class="form-group providers-services-group">
            <label class="providers-services-label">Serviços</label>
            <p class="field-hint">Selecione os serviços que esta pessoa pode realizar.</p>
            <div class="providers-service-filter-wrap">
              <label for="provider-service-search" class="providers-service-filter-label">Filtrar</label>
              <input
                id="provider-service-search"
                v-model="serviceFilter"
                type="search"
                class="providers-service-filter-input"
                placeholder="Nome do serviço"
                aria-label="Filtrar por nome"
                autocomplete="off"
              />
              <i class="material-icons providers-service-filter-icon" aria-hidden="true">search</i>
            </div>
            <div class="providers-service-list">
              <div
                v-for="svc in filteredServices"
                :key="svc.id"
                class="providers-service-item"
                :class="{ 'providers-service-item--selected': selectedServiceIds.includes(svc.id) }"
              >
                <label class="providers-service-item-main">
                  <input
                    type="checkbox"
                    :value="svc.id"
                    :checked="selectedServiceIds.includes(svc.id)"
                    @change="toggleService(svc.id)"
                    class="providers-service-checkbox"
                  />
                  <span class="providers-service-name">{{ svc.name }}</span>
                </label>
                <div
                  v-if="selectedServiceIds.includes(svc.id)"
                  class="providers-service-percent"
                >
                  <label class="providers-service-percent-label" :for="'svc-pct-' + svc.id">
                    % a pagar
                  </label>
                  <input
                    :id="'svc-pct-' + svc.id"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="providers-service-percent-input"
                    :value="servicePercentOverrides[svc.id] ?? ''"
                    placeholder="Padrão"
                    aria-label="Percentual a pagar neste serviço"
                    @input="setServicePercentOverride(svc.id, $event.target.value)"
                  />
                  <p class="providers-service-percent-hint">
                    <template v-if="servicePercentOverrides[svc.id] === '' || servicePercentOverrides[svc.id] == null">
                      Usa padrão: {{ defaultPercentToPayLabel }}%
                    </template>
                    <template v-else>
                      Valor específico (padrão: {{ defaultPercentToPayLabel }}%)
                    </template>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group working-hours-group">
            <label class="working-hours-group-label">Horário de trabalho por dia</label>
            <p class="field-hint">Marque "Fechado" nos dias sem atendimento. Nos dias abertos, defina os horários.</p>
            <div class="working-hours-list">
              <div
                v-for="row in dayRows"
                :key="row.day"
                class="working-hours-day-block"
                :class="{ 'working-hours-day-block--closed': row.closed }"
              >
                <div class="working-hours-day-header">
                  <span class="working-hours-day-name">{{ row.label }}</span>
                  <label class="working-hours-closed-label">
                    <input
                      type="checkbox"
                      :checked="row.closed"
                      @change="setDayClosed(row.day, $event.target.checked)"
                      class="providers-service-checkbox"
                    />
                    <span>Fechado</span>
                  </label>
                </div>
                <div v-if="!row.closed" class="working-hours-day-body">
                  <div
                    v-for="(range, idx) in row.ranges"
                    :key="idx"
                    class="working-hours-range"
                  >
                    <input
                      type="time"
                      v-model="range.start"
                      class="working-hours-input"
                      placeholder="07:00"
                      aria-label="Início"
                    />
                    <span class="working-hours-sep">até</span>
                    <input
                      type="time"
                      v-model="range.end"
                      class="working-hours-input"
                      placeholder="20:30"
                      aria-label="Fim"
                    />
                    <button
                      type="button"
                      class="working-hours-remove"
                      aria-label="Remover horário"
                      title="Remover"
                      @click="removeRange(row.day, idx)"
                    >
                      <i class="material-icons">close</i>
                    </button>
                  </div>
                  <button type="button" class="working-hours-add-btn" @click="addRange(row.day)">
                    <i class="material-icons">add</i>
                    Adicionar horário
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="canAssignRoles" class="form-group providers-roles-group">
            <label class="providers-roles-label">Perfis</label>
            <p class="field-hint">Atribua um ou mais perfis a esta pessoa. As permissões são atualizadas automaticamente.</p>
            <div class="providers-roles-list">
              <label
                v-for="role in sortedRoles"
                :key="role.id"
                class="providers-role-item"
              >
                <input
                  type="checkbox"
                  :value="role.id"
                  :checked="selectedRoleIds.includes(role.id)"
                  @change="toggleRole(role.id)"
                  class="providers-service-checkbox"
                />
                <span class="providers-role-name">{{ role.name || role.id }}</span>
              </label>
            </div>
          </div>
          <p v-if="saveError" class="field-error" role="alert">{{ saveError }}</p>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>
  </div>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import { doc, setDoc, collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useServiceStore } from '@/stores/service';
import { useToast } from '@/composables/useToast';
import {
  assignedServiceOverridesFromUser,
  buildAssignedServicesPayload,
  getAssignedServices,
  isValidProviderPercentInput,
  normalizePercentToPay,
} from '@/composables/useProviderPercent';

const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const DEFAULT_USER_COLOR = '#ECD2D2';

const COLOR_PRESETS = [
  '#ECD2D2',
  '#DBC1C1',
  '#B8D4E3',
  '#b07d7d',
  '#7db0b0',
  '#b0a07d',
  '#a07db0',
  '#7db07d',
];

export default {
  name: 'Providers',
  components: { Drawer },
  setup() {
    const userStore = useUserStore();
    const serviceStore = useServiceStore();
    const { show: showToast } = useToast();
    const showModal = ref(false);
    const editingUser = ref(null);
    const selectedServiceIds = ref([]);
    const servicePercentOverrides = ref({});
    const selectedRoleIds = ref([]);
    const serviceFilter = ref('');
    const roles = ref({});
    const saveLoading = ref(false);
    const saveError = ref('');
    let unsubRoles = null;

    const canAssignRoles = computed(() => userStore.hasPermission('roles:manage'));

    const sortedRoles = computed(() => {
      const list = Object.entries(roles.value).map(([id, data]) => ({ id, ...data }));
      return list.sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''));
    });

    const allServices = computed(() => {
      const list = Object.entries(serviceStore.services).map(([id, data]) => ({ id, ...data }));
      return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    const filteredServices = computed(() => {
      const q = (serviceFilter.value || '').trim().toLowerCase();
      if (!q) return allServices.value;
      return allServices.value.filter((s) => (s.name || '').toLowerCase().includes(q));
    });

    const sortedUsers = computed(() => {
      const list = Object.entries(userStore.users).map(([id, data]) => ({ id, ...data }));
      return list.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
    });

    const defaultPercentToPayLabel = computed(() => {
      if (!editingUser.value) return 100;
      return normalizePercentToPay(editingUser.value.percentToPay, 100);
    });

    const dayRows = ref([]);

    function buildDayRows(workingHours) {
      const wh = workingHours || {};
      return DAY_LABELS.map((label, day) => {
        const d = String(day);
        let raw = wh[d];
        if (raw != null && !Array.isArray(raw) && typeof raw === 'object' && (raw.start != null || raw.end != null)) {
          raw = [raw];
        }
        const ranges = Array.isArray(raw)
          ? raw.filter((r) => r && (r.start != null || r.end != null)).map((r) => ({ start: r.start || '', end: r.end || '' }))
          : [];
        const closed = ranges.length === 0;
        return { day: d, label, ranges: ranges.length ? ranges : [{ start: '', end: '' }], closed };
      });
    }

    function openEdit(user) {
      serviceFilter.value = '';
      editingUser.value = {
        id: user.id,
        name: user.name,
        email: user.email,
        color: user.color || DEFAULT_USER_COLOR,
        percentToPay: user.percentToPay != null ? user.percentToPay : 100,
        assignedServices: getAssignedServices(user),
        serviceIds: Array.isArray(user.serviceIds) ? [...user.serviceIds] : [],
        roleIds: Array.isArray(user.roleIds) ? [...user.roleIds] : [],
        workingHours: user.workingHours && typeof user.workingHours === 'object'
          ? { ...user.workingHours }
          : {},
      };
      const assigned = editingUser.value.assignedServices || [];
      selectedServiceIds.value = assigned.length
        ? assigned.map((a) => a.serviceId)
        : editingUser.value.serviceIds.slice();
      const overrides = assignedServiceOverridesFromUser(user);
      const overrideState = {};
      for (const id of selectedServiceIds.value) {
        if (overrides[id] != null) overrideState[id] = String(overrides[id]);
      }
      servicePercentOverrides.value = overrideState;
      selectedRoleIds.value = editingUser.value.roleIds.slice();
      dayRows.value = buildDayRows(editingUser.value.workingHours);
      saveError.value = '';
      showModal.value = true;
    }

    function toggleRole(roleId) {
      const idx = selectedRoleIds.value.indexOf(roleId);
      if (idx === -1) {
        selectedRoleIds.value = [...selectedRoleIds.value, roleId].sort();
      } else {
        selectedRoleIds.value = selectedRoleIds.value.filter((id) => id !== roleId);
      }
    }

    function toggleService(serviceId) {
      const idx = selectedServiceIds.value.indexOf(serviceId);
      if (idx === -1) {
        selectedServiceIds.value = [...selectedServiceIds.value, serviceId];
      } else {
        selectedServiceIds.value = selectedServiceIds.value.filter((id) => id !== serviceId);
        const next = { ...servicePercentOverrides.value };
        delete next[serviceId];
        servicePercentOverrides.value = next;
      }
    }

    function setServicePercentOverride(serviceId, rawValue) {
      const next = { ...servicePercentOverrides.value };
      if (rawValue === '' || rawValue == null) {
        delete next[serviceId];
      } else {
        next[serviceId] = rawValue;
      }
      servicePercentOverrides.value = next;
    }

    function setDayClosed(day, closed) {
      const r = dayRows.value.find((x) => x.day === day);
      if (r) {
        r.closed = closed;
        if (closed) {
          r.ranges = [];
        } else if (r.ranges.length === 0) {
          r.ranges = [{ start: '', end: '' }];
        }
      }
    }

    function addRange(day) {
      const r = dayRows.value.find((x) => x.day === day);
      if (r && !r.closed) {
        r.ranges = [...r.ranges, { start: '', end: '' }];
      }
    }

    function removeRange(day, index) {
      const r = dayRows.value.find((x) => x.day === day);
      if (r && !r.closed && r.ranges.length > index) {
        r.ranges = r.ranges.filter((_, i) => i !== index);
        if (r.ranges.length === 0) {
          r.ranges = [{ start: '', end: '' }];
        }
      }
    }

    async function save() {
      if (!editingUser.value) return;
      saveError.value = '';
      for (const id of selectedServiceIds.value) {
        const raw = servicePercentOverrides.value[id];
        if (raw !== '' && raw != null && !isValidProviderPercentInput(raw)) {
          saveError.value = 'Percentuais por serviço devem ser números inteiros entre 0 e 100.';
          return;
        }
      }
      const serviceIds = selectedServiceIds.value.filter(Boolean);
      const workingHours = {};
      dayRows.value.forEach((r) => {
        if (r.closed) {
          workingHours[r.day] = [];
        } else {
          workingHours[r.day] = (r.ranges || [])
            .filter((range) => range.start && range.end)
            .map((range) => ({ start: range.start, end: range.end }));
        }
      });
      saveLoading.value = true;
      try {
        let color = (editingUser.value.color || '').trim();
        if (/^[0-9A-Fa-f]{6}$/.test(color)) color = '#' + color;
        const rawPercent = Number(editingUser.value.percentToPay);
        const percentToPay = Number.isFinite(rawPercent)
          ? Math.max(0, Math.min(100, Math.round(rawPercent)))
          : 100;
        const { assignedServices, serviceIds: derivedServiceIds } = buildAssignedServicesPayload(
          serviceIds,
          servicePercentOverrides.value,
          percentToPay
        );
        const payload = {
          serviceIds: derivedServiceIds,
          assignedServices,
          workingHours,
          color: /^#[0-9A-Fa-f]{6}$/.test(color) ? color : DEFAULT_USER_COLOR,
          percentToPay,
        };
        if (canAssignRoles.value) {
          payload.roleIds = selectedRoleIds.value.slice();
        }
        await setDoc(
          doc(firestore, 'users', editingUser.value.id),
          payload,
          { merge: true }
        );
        showModal.value = false;
        showToast('Salvo.', 'success');
      } catch (err) {
        saveError.value = 'Não foi possível salvar. Tente novamente.';
        console.warn('Providers save failed', err);
      } finally {
        saveLoading.value = false;
      }
    }

    onMounted(() => {
      if (userStore.hasPermission('roles:manage')) {
        unsubRoles = onSnapshot(collection(firestore, 'roles'), (snapshot) => {
          const next = {};
          snapshot.docs.forEach((d) => {
            next[d.id] = { id: d.id, ...d.data() };
          });
          roles.value = next;
        }, (err) => console.error('Providers roles snapshot', err));
      }
    });

    onUnmounted(() => {
      if (typeof unsubRoles === 'function') unsubRoles();
    });

    const colorPresets = COLOR_PRESETS;

    return {
      showModal,
      editingUser,
      colorPresets,
      selectedServiceIds,
      servicePercentOverrides,
      defaultPercentToPayLabel,
      selectedRoleIds,
      dayRows,
      sortedUsers,
      sortedRoles,
      allServices,
      filteredServices,
      serviceFilter,
      saveLoading,
      saveError,
      canAssignRoles,
      openEdit,
      toggleService,
      setServicePercentOverride,
      toggleRole,
      setDayClosed,
      addRange,
      removeRange,
      save,
    };
  },
};
</script>

<style scoped>
.providers-page {
  padding: var(--space-md) 0;
}

.providers-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md, 8px);
  min-width: 3rem;
}

.providers-card-icon .material-icons {
  font-size: 2rem;
  opacity: 0.9;
  color: rgba(0, 0, 0, 0.6);
}

.providers-percent-group {
  margin-bottom: var(--space-lg);
}

.providers-percent-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.providers-percent-input {
  width: 6rem;
  max-width: 120px;
  margin-top: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  font-size: 1rem;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  background: var(--color-surface);
  color: var(--color-text);
}

.providers-percent-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.providers-color-group {
  margin-bottom: var(--space-lg);
}

.providers-color-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.providers-color-picker-wrap {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-top: var(--space-sm);
}

.providers-color-input {
  width: 3rem;
  height: 2.5rem;
  padding: 2px;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  background: transparent;
}

.providers-color-input::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.providers-color-input::-webkit-color-swatch {
  border: none;
  border-radius: 2px;
}

.providers-color-hex {
  flex: 1;
  max-width: 8rem;
  padding: var(--space-xs) var(--space-sm);
  font-size: 0.875rem;
  font-family: monospace;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  background: var(--color-surface);
  color: var(--color-text);
}

.providers-color-presets {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
  margin-top: var(--space-sm);
}

.providers-color-preset {
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 2px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
}

.providers-color-preset:hover {
  border-color: var(--color-accent);
  transform: scale(1.1);
}

.providers-services-group {
  margin-bottom: var(--space-lg);
}

.providers-services-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.providers-service-filter-wrap {
  position: relative;
  margin-top: var(--space-md);
  margin-bottom: var(--space-sm);
}

.providers-service-filter-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.providers-service-filter-input {
  width: 100%;
  padding: 10px 12px 10px 44px;
  font-size: 1rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.providers-service-filter-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.providers-service-filter-input::placeholder {
  color: var(--color-text-muted);
}

.providers-service-filter-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.providers-service-list {
  margin-top: var(--space-sm);
}

.providers-service-item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-xs);
  font-weight: normal;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-sm, 4px);
  border: 1px solid transparent;
}

.providers-service-item-main {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
}

.providers-service-percent {
  margin-left: calc(1.125rem + var(--space-sm));
  padding-bottom: var(--space-xs);
}

.providers-service-percent-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-secondary, #666);
  margin-bottom: 2px;
}

.providers-service-percent-input {
  width: 6rem;
  max-width: 120px;
  padding: var(--space-xs) var(--space-sm);
  font-size: 0.9375rem;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  background: var(--color-surface);
  color: var(--color-text);
}

.providers-service-percent-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.providers-service-percent-hint {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.providers-service-item:hover {
  border-color: var(--color-text-secondary, #999);
  background: var(--color-bg-soft, #f8f8f8);
}

.providers-service-item--selected {
  background: var(--color-accent-soft);
}

.providers-service-item--selected:hover {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
}

.providers-service-checkbox {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  accent-color: var(--color-accent);
  cursor: pointer;
}

.providers-service-name {
  font-size: 0.9375rem;
  color: var(--color-text);
  flex: 1;
  min-width: 0;
}

.working-hours-group {
  margin-top: var(--space-sm);
}

.providers-roles-group {
  margin-top: var(--space-lg);
}

.providers-roles-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.providers-roles-list {
  margin-top: var(--space-md);
}

.providers-role-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  font-weight: normal;
  padding: var(--space-sm) var(--space-md);
}

.providers-role-item:hover {
  background: var(--color-bg-soft, #f8f8f8);
}

.providers-role-name {
  font-size: 0.9375rem;
  color: var(--color-text);
}

.working-hours-group-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.working-hours-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-top: var(--space-md);
}

.working-hours-day-block {
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-sm) var(--space-md);
  transition: background 0.15s, border-color 0.15s;
}

.working-hours-day-block--closed {
  padding: var(--space-sm) var(--space-md);
}

.working-hours-day-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  min-height: 36px;
}

.working-hours-day-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--color-text);
  min-width: 2.5rem;
}

.working-hours-closed-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: 0.875rem;
  font-weight: normal;
  color: var(--color-text-secondary, #666);
  cursor: pointer;
  user-select: none;
}

.working-hours-day-body {
  margin-top: var(--space-sm);
  padding-top: var(--space-sm);
  border-top: 1px solid var(--color-border, #e8e8e8);
}

.working-hours-range {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.working-hours-range:last-of-type {
  margin-bottom: 0;
}

.working-hours-input {
  width: 7rem;
  max-width: 120px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius-sm, 4px);
  font-size: 0.875rem;
  background: #fff;
}

.working-hours-sep {
  font-size: 0.8125rem;
  color: var(--color-text-secondary, #666);
  flex-shrink: 0;
}

.working-hours-remove {
  padding: var(--space-xs);
  border: none;
  background: none;
  color: var(--color-text-secondary, #666);
  cursor: pointer;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
}

.working-hours-remove:hover {
  color: var(--color-error, #c62828);
  background: rgba(0, 0, 0, 0.04);
}

.working-hours-remove .material-icons {
  font-size: 20px;
}

.working-hours-add-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  margin-top: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  font-size: 0.8125rem;
  color: var(--color-accent);
  background: none;
  border: 1px dashed var(--color-border, #ccc);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.working-hours-add-btn:hover {
  border-color: var(--color-accent);
  background: var(--color-accent-soft, rgba(0, 0, 0, 0.02));
}

.working-hours-add-btn .material-icons {
  font-size: 18px;
}

.field-hint {
  font-size: 0.875rem;
  color: var(--color-text-secondary, #666);
  margin-top: 2px;
  margin-bottom: 0;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-sm);
  margin-bottom: 0;
}
</style>
